import React from 'react';
import { Avatar, Box, Button, Chip, CircularProgress, Paper, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import { LiveRoomState } from '../../../types/liveQuiz';
import { useServerCountdown } from '../../../hooks/useServerCountdown';
import { formatCountdown } from '../../../utils/countdownFormat';

interface LiveQuizLobbyProps {
  state: LiveRoomState;
  /**
   * Skew-corrected countdown to `state.phaseEndsAt`, already computed by the
   * caller's `useLiveQuizRoom` (which re-anchors on every payload and is the
   * highest-stakes timer in the product). Used for what's displayed.
   */
  remainingMs: number | null;
  currentUserId?: number;
  quizTitle?: string;
  joining: boolean;
  onExpire?: () => void;
}

const ROSTER_AVATAR_COLORS = ['#4C6EF5', '#F76707', '#37B24D', '#AE3EC9', '#F03E3E', '#1098AD'];

/**
 * Waiting-room panel, split from the old collapsed `scheduled || lobby`
 * branch into two purpose-built states.
 *
 * A second, independent `useServerCountdown` drives only `onExpire` here —
 * any request advances the (unscheduled) room state machine, so crossing
 * either deadline should prompt the caller to refetch once. The visible
 * countdown itself still comes from the `remainingMs` prop rather than this
 * hook's own reading, so the room's own clock skew correction (re-anchored
 * every payload) stays the single source of truth for what's on screen.
 */
const LiveQuizLobby: React.FC<LiveQuizLobbyProps> = ({
  state,
  remainingMs,
  currentUserId,
  quizTitle,
  joining,
  onExpire
}) => {
  const theme = useTheme();
  const isScheduled = state.status === 'scheduled';
  const isLobby = state.status === 'lobby';

  const targetIso = isScheduled || isLobby ? state.phaseEndsAt : null;
  useServerCountdown(targetIso, { onExpire });

  const countdownLabel = formatCountdown(Math.max(0, remainingMs ?? 0));

  if (isScheduled) {
    const opensAt = state.phaseEndsAt
      ? new Date(state.phaseEndsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : null;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <ScheduleIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
        {quizTitle && (
          <Typography variant="overline" color="text.secondary">
            {quizTitle}
          </Typography>
        )}
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          This quiz hasn&apos;t opened yet
        </Typography>
        {opensAt && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Opens at {opensAt}
          </Typography>
        )}
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 800,
            fontSize: '2rem'
          }}
        >
          {countdownLabel}
        </Typography>
        <Button component={RouterLink} to="/student/quizzes" sx={{ mt: 3, textTransform: 'none' }}>
          Back to quizzes
        </Button>
      </Paper>
    );
  }

  if (!isLobby) {
    // Any other phase is someone else's screen to render.
    return null;
  }

  const participants = state.participants;
  const viewerName = participants?.find(p => p.userId === currentUserId)?.displayName;
  const hiddenCount = participants ? Math.max(0, state.participantCount - participants.length) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 5,
        textAlign: 'center',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        Starting in
      </Typography>
      <Typography
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 800,
          fontSize: '2.5rem',
          mb: 3
        }}
      >
        {countdownLabel}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
        {joining ? (
          <>
            <CircularProgress size={16} />
            <Typography fontWeight={600}>Joining…</Typography>
          </>
        ) : (
          <>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography fontWeight={700}>
              {viewerName ? `You're in, ${firstNameOf(viewerName)}!` : "You're in!"}
            </Typography>
          </>
        )}
      </Box>

      {participants ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 3 }}>
          <AnimatePresence>
            {participants.map((participant, index) => (
              <motion.div
                key={participant.userId}
                layout
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              >
                <Tooltip title={participant.displayName}>
                  <Avatar
                    data-testid="lobby-roster-avatar"
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: '0.8rem',
                      bgcolor: ROSTER_AVATAR_COLORS[index % ROSTER_AVATAR_COLORS.length]
                    }}
                  >
                    {initialsOf(participant.displayName)}
                  </Avatar>
                </Tooltip>
              </motion.div>
            ))}
          </AnimatePresence>
          {hiddenCount > 0 && (
            <Avatar sx={{ width: 36, height: 36, fontSize: '0.75rem', bgcolor: alpha(theme.palette.text.primary, 0.2) }}>
              +{hiddenCount}
            </Avatar>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
          <GroupIcon color="action" />
          <Typography color="text.secondary">
            {state.participantCount} mathlete{state.participantCount === 1 ? '' : 's'} ready
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {state.totalQuestions} question{state.totalQuestions === 1 ? '' : 's'} · faster answers
          score more
        </Typography>
        <Chip label="Counts for your rating" size="small" color="primary" variant="outlined" />
      </Box>
    </Paper>
  );
};

/** "mayachen" -> "MA". Falls back to a single initial for a one-letter name. */
function initialsOf(displayName: string): string {
  return displayName.slice(0, 2).toUpperCase();
}

/** Display names in this app are single-token handles, but split defensively. */
function firstNameOf(displayName: string): string {
  return displayName.split(' ')[0];
}

export default LiveQuizLobby;
